/**
 * Demo fixtures — the mock "backend" route table behind demo-backend's fetch
 * interceptor.
 *
 * Each route maps (METHOD, path-regex) -> simulated JSON. `path` is the portion
 * of the URL from `/api/` onward with the query stripped (e.g. `/api/v1/me`,
 * `/api/v2/employee/tara/proposals`). First match wins; unmatched API calls get
 * a safe empty default so free exploration never crashes or triggers a
 * "session expired" logout.
 *
 * NOTE: handlers are populated per feature area once the real response shapes
 * are confirmed (see demo-data.ts for the simulated content).
 */
interface DemoCtx {
  path: string;
  /** Full request URL (with query). */
  url: string;
  /** Parsed query params from the request URL. */
  query: URLSearchParams;
  method: string;
  body: unknown;
  match: RegExpExecArray;
}
type DemoHandler = (ctx: DemoCtx) => unknown;
interface DemoRoute {
  /** HTTP method, or '*' for any. */
  method: string;
  /** Tested against the `/api/...` path. */
  test: RegExp;
  handler: DemoHandler;
}

const extractApiPath = (url: string): string => {
  const q = url.indexOf('?');
  const noQuery = q === -1 ? url : url.slice(0, q);
  const idx = noQuery.indexOf('/api/');
  return idx === -1 ? noQuery : noQuery.slice(idx);
};

/**
 * Route table — populated from demo-routes once shapes are confirmed. Kept as a
 * mutable export target so feature modules can register their fixtures.
 */
export const demoRoutes: DemoRoute[] = [];

export const registerDemoRoutes = (routes: DemoRoute[]): void => {
  demoRoutes.push(...routes);
};

export const resolveDemoResponse = (
  url: string,
  method: string,
  body: unknown
): { payload: unknown; status?: number } => {
  const path = extractApiPath(url);
  let query: URLSearchParams;
  try {
    const q = url.indexOf('?');
    query = new URLSearchParams(q === -1 ? '' : url.slice(q + 1));
  } catch {
    query = new URLSearchParams('');
  }
  for (const route of demoRoutes) {
    if (route.method !== '*' && route.method !== method) continue;
    const match = route.test.exec(path);
    if (match) {
      return { payload: route.handler({ path, url, query, method, body, match }) };
    }
  }
  // Unknown API call during free roam — a success envelope with an empty list.
  // This satisfies bespoke fetch clients (unwrap -> []) AND asyncHandler clients
  // (which check `success` and otherwise return [] or throw).
  return { payload: { success: true, data: [] } };
};

export type { DemoCtx, DemoHandler, DemoRoute };
